import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import MetaPhaseSymbol from '../brand/MetaPhaseSymbol'
import AgencyMark from '../marks/AgencyMark'
import Identifier from './Identifier'
import { primaryLinks } from '@/content/navigation'
import { COPYRIGHT_YEAR, OPERATOR } from '../../content/provenance'
import { america, findMission } from '@/content/missions'
import { usePrefs, type Prefs } from '@/state/prefs'
import Editable from '../inspector/Editable'
import { footerHeadingOptions, footerListOptions, footerOptions, footerRuleOptions, identifierOptions, socialStyleOptions } from '../home/options'
import { agencySocial, operatorSocial } from '@/content/socials'
import { useBrand } from '@/state/useBrand'
import Cased from '@/components/ui/Cased'
import HomeLink from './HomeLink'
import { useIsHome } from '@/state/useIsHome'

const policyLinks = [
  { to: '/about', label: 'About' },
  { to: '/accessibility', label: 'Accessibility' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/disclaimer', label: 'Disclaimer' },
  { to: '/credits', label: 'Credits' },
  { to: '/license', label: 'License' },
]

const columns = [
  {
    heading: 'Build',
    links: [
      { to: '/getting-started', label: 'Get Started' },
      { to: '/components', label: 'Components' },
      { to: '/templates', label: 'Templates' },
      { to: '/patterns', label: 'Patterns' },
      { to: '/agents', label: 'Agents' },
    ],
  },
  {
    heading: 'Design',
    links: [
      { to: '/foundations', label: 'Foundations' },
      { to: '/foundations/typography', label: 'Typography' },
      { to: '/foundations/identity', label: 'Identity' },
      { to: '/missions', label: 'Missions' },
    ],
  },
]

function Maker() {
  return (
    <p className="mds-footer__maker">
      <MetaPhaseSymbol className="mds-footer__symbol" />
      <span>
        Built by <a href={OPERATOR.url}>{OPERATOR.name}</a>
      </span>
      <span className="mds-footer__copyright">© {COPYRIGHT_YEAR} All Rights Reserved</span>
    </p>
  )
}

const socialNetworks = [
  {
    key: 'linkedin',
    label: 'LinkedIn',
    path: 'M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13ZM7.12 20.45H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0Z',
  },
  {
    key: 'x',
    label: 'X',
    path: 'M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.65l-5.21-6.82-5.97 6.82H1.68l7.73-8.84L1.25 2.25h6.83l4.71 6.23 5.45-6.23Zm-1.16 17.52h1.83L7.08 4.13H5.12l11.96 15.64Z',
  },
] as const

/** The site's social accounts: the agency's own, or MetaPhase's on the default theme. */
function useSocial() {
  const { prefs } = usePrefs()
  const mission = findMission(prefs.mission) ?? america
  const owner = mission.id === america.id ? OPERATOR.name : mission.short
  const links = mission.id === america.id ? operatorSocial : (agencySocial[mission.id] ?? {})
  const shown = socialNetworks.filter((network) => links[network.key])
  return shown.length ? { owner, links, shown } : null
}

function Social() {
  const social = useSocial()
  const { prefs, set } = usePrefs()
  const isHome = useIsHome()
  if (!social) return null
  // With None the links are hidden, and the note stays so they can return.
  const hidden = prefs.socialStyle === 'none'
  if (hidden && !isHome) return null
  return (
    <Editable
      label="Social"
      optionsLabel="Style"
      value={prefs.socialStyle}
      options={socialStyleOptions}
      onChange={(value) => set('socialStyle', value)}
      token="footer social"
      pin="below-left"
      as="span"
      className="mds-footer__social-note"
      enabled={isHome}
    >
      {hidden ? (
        <span className="mds-footer__social-empty" />
      ) : (
        <ul className={`mds-footer__social mds-footer__social--${prefs.socialStyle}`} aria-label={`${social.owner} on social media`}>
          {social.shown.map((network) => (
            <li key={network.key}>
              <a href={social.links[network.key]} aria-label={`${social.owner} on ${network.label}`}>
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor">
                  <path d={network.path} />
                </svg>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Editable>
  )
}

/** The social accounts as a footer column, beside the link columns. */
function SocialColumn() {
  const social = useSocial()
  const { prefs } = usePrefs()
  const isHome = useIsHome()
  const hidden = prefs.socialStyle === 'none'
  if (!social || (hidden && !isHome)) return null
  return (
    <div className="mds-footer__column">
      {!hidden && (
        <h2 className="mds-footer__heading">
          <Cased>Follow</Cased>
        </h2>
      )}
      <Social />
    </div>
  )
}

function Policies() {
  return (
    <nav aria-label="Policies" className="mds-footer__policies">
      {policyLinks.map((link) => (
        <Link key={link.to} to={link.to}>
          {link.label}
        </Link>
      ))}
    </nav>
  )
}

function Columns({ heading }: { heading?: boolean }) {
  const { name } = useBrand()
  const { prefs, set } = usePrefs()
  const isHome = useIsHome()
  const notes = isHome
  return (
    <nav aria-label="Footer" className="mds-footer__columns">
      {heading && (
        <HomeLink className="mds-footer__wordmark" aria-label={`${name} home`}>
          {name}
        </HomeLink>
      )}
      {columns.map((column, index) => {
        const title = (
          <h2 className="mds-footer__heading">
            <Cased>{column.heading}</Cased>
          </h2>
        )
        const list = (
          <ul>
            {column.links.map((link) => (
              <li key={link.to}>
                <Link to={link.to}>{link.label}</Link>
              </li>
            ))}
          </ul>
        )
        // The first column carries the notes for every column's heading and list.
        return (
          <div key={column.heading} className="mds-footer__column">
            {index === 0 ? (
              <Editable
                label="Footer heading"
                value={prefs.footerHeading}
                options={footerHeadingOptions}
                onChange={(value) => set('footerHeading', value)}
                token="data-footer-heading"
                pin="top-left"
                className="mds-footer__heading-note"
                enabled={notes}
              >
                {title}
              </Editable>
            ) : (
              title
            )}
            {index === 0 ? (
              <Editable
                label="List style"
                value={prefs.footerList}
                options={footerListOptions}
                onChange={(value) => set('footerList', value)}
                token="data-footer-list"
                pin="right"
                className="mds-footer__list-note"
                enabled={notes}
              >
                {list}
              </Editable>
            ) : (
              list
            )}
          </div>
        )
      })}
      <SocialColumn />
    </nav>
  )
}

/**
 * The agency's footer, in one of four layouts: editorial (America.gov),
 * columns (USA.gov), dark (WhiteHouse.gov) and studio (National Design
 * Studio). Social links and, for an agency, the identifier sit inside it.
 */
function FooterBody() {
  const { prefs } = usePrefs()
  const mission = findMission(prefs.mission) ?? america
  // The identifier belongs to an agency site, so the default theme has none.
  const identifier = mission.id !== america.id && prefs.footerIdentifier === 'on' && <Identifier mission={mission} />
  const style = prefs.footer

  if (style === 'studio')
    return (
      <div className="mds-container">
        {identifier}
        <div className="mds-footer__studio">
          <nav aria-label="Footer" className="mds-footer__policies">
            {/* Get Started is the call to action beside these, so it is not repeated. */}
            {primaryLinks.filter((link) => link.to !== '/getting-started').slice(0, 4).map((link) => (
              <Link key={link.to} to={link.to}>
                {link.label}
              </Link>
            ))}
          </nav>
          <Social />
          <Link className="mds-footer__updates" to="/getting-started">
            Get Started →
          </Link>
        </div>
      </div>
    )

  if (style === 'columns' || style === 'dark')
    return (
      <div className="mds-container">
        <Columns heading={style === 'dark'} />
        {identifier}
      </div>
    )

  return (
    <div className="mds-container mds-footer__editorial">
      <nav aria-label="Footer" className="mds-footer__links">
        {primaryLinks.map((link) => (
          <Link key={link.to} to={link.to}>
            {link.label}
          </Link>
        ))}
      </nav>
      <Social />
      {identifier}
    </div>
  )
}

/** The MDS bar under every footer: the same policies and credit on every page and theme. */
/**
 * The bar under every footer is Mission Design System's own, with MetaPhase's
 * credit: it keeps the flag and the system's name whatever mission, mark or
 * name the visitor chose for the rest of the page.
 */
function SiteBar() {
  // On the home page the bar rises in each time it comes into view, a beat
  // after the footer above it, and sinks back as it leaves, which sets it apart
  // as its own strip. It starts visible, and waits only once the browser can
  // say when it is reached; elsewhere it simply shows.
  const bar = useRef<HTMLDivElement>(null)
  const isHome = useIsHome()
  const [stage, setStage] = useState<'still' | 'waiting' | 'shown' | 'leaving'>('still')
  const home = isHome
  useEffect(() => {
    const element = bar.current
    if (!home || !element || typeof IntersectionObserver === 'undefined') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    // Hidden at once until first seen; after that it fades out as it leaves.
    const observer = new IntersectionObserver(
      ([entry]) => setStage((current) => (entry.isIntersecting ? 'shown' : current === 'shown' ? 'leaving' : current === 'leaving' ? 'leaving' : 'waiting')),
      { threshold: 0.4 },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [home])
  return (
    <div ref={bar} className={`mds-site-bar${home && stage !== 'still' ? ` is-${stage}` : ''}`}>
      <div className="mds-container mds-site-bar__inner">
        <HomeLink className="mds-footer__home" aria-label="Mission Design System home">
          <span className="mds-lockup mds-lockup--compact mds-lockup--system" data-mission={america.id}>
            <AgencyMark mission={america} variant="flag" className="mds-lockup__mark" />
            <span className="mds-lockup__text">
              <span className="mds-lockup__word">Mission Design System</span>
            </span>
          </span>
        </HomeLink>
        <Policies />
        <Maker />
      </div>
    </div>
  )
}

export default function SiteFooter() {
  const { prefs, set } = usePrefs()
  const isHome = useIsHome()
  return (
    <footer className={`mds-site-footer mds-site-footer--rule-${prefs.footerRule}`}>
      {/* The separator has its own note, on the line above the footer. */}
      <Editable
        label="Separator"
        value={prefs.footerRule}
        options={footerRuleOptions}
        onChange={(value) => set('footerRule', value)}
        token="footer separator"
        pin="right"
        as="span"
        className="mds-footer__rule-note"
        enabled={isHome}
      >
        <span />
      </Editable>
      <div className={`mds-footer mds-footer--${prefs.footer}`} {...(prefs.footer === 'dark' ? { 'data-theme': 'dark', 'data-mission': prefs.mission } : {})}>
        <Editable
          label="Footer"
          value={prefs.footer}
          options={footerOptions}
          onChange={(value) => set('footer', value)}
          optionsLabel="Layout"
          more={[
            // The identifier is an agency pattern, so the default theme has no option for it.
            ...(prefs.mission === america.id
              ? []
              : [
                  {
                    label: 'Agency identifier',
                    value: prefs.footerIdentifier,
                    options: identifierOptions,
                    onChange: (value: string) => set('footerIdentifier', value as Prefs['footerIdentifier']),
                  },
                ]),
          ]}
          token="footer"
          pin="inside-top-right"
          className="mds-editable--region"
          enabled={isHome}
        >
          <FooterBody />
        </Editable>
      </div>
      <SiteBar />
    </footer>
  )
}
